import { useChat } from '@ai-sdk/react';
import { FitAddon } from '@xterm/addon-fit';
import type RFB from '@novnc/novnc';
import type { RfbDisconnectEvent } from '@novnc/novnc';
import { Terminal } from '@xterm/xterm';
import {
  isDynamicToolUIPart,
  isTextUIPart,
  type DynamicToolUIPart,
  type UIMessage,
  type UIMessageChunk,
} from 'ai';
import {
  AuthLayout,
  Button,
  CyrusBrand,
  Input,
} from '@cyrusagents/design-system';
import { Key } from 'iconoir-react';
import {
  FormEvent,
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  api,
  cancelCustomerJob,
  closeCustomerSurface,
  createCustomerJob,
  deleteSession,
  developmentSignIn,
  getAuthSession,
  getCurrentCustomerDeployment,
  getCustomerJobEncryption,
  getCustomerJobEvents,
  getGithubAppStatus,
  getMessages,
  getSlackAppStatus,
  keepCustomerSurfaceAlive,
  listCustomerJobs,
  listSessions,
  openCustomerSurface,
  passwordSignIn,
  sessionHeaders,
  sessionAction,
  signOut,
  type AuthSession,
  type AuthUser,
  type CustomerDeploymentStatus,
  type CustomerJob,
  type CustomerSurfaceConnection,
  type GithubAppStatus,
  type Provider,
  type PublicSession,
  type RuntimeFeatures,
  type SlackAppStatus,
} from './api.js';
import { CustomerDeploymentDialog } from './customer-deployment.js';
import { CustomerPolicyDialog } from './customer-policy-dialog.js';
import {
  coalesceOutput,
  CustomerJobsDialog,
  decryptEvent,
  mergeEvents,
  type AgentOutputEntry,
  type DecryptedEvent,
} from './customer-jobs.js';
import {
  decryptCustomerPayload,
  encryptCustomerJobPayload,
  rememberCustomerJobKey,
} from './customer-job-crypto.js';
import { CyrusChatTransport } from './cyrus-chat-transport.js';
import { getAppIconUrl, type AppIconName, type AppIconSurface } from './appIcons.js';

type ChatSeed = {
  key: string;
  sessionId: string | null;
  messages: UIMessage[];
  lastEventId: number;
};

type PromptCacheProof = {
  generatedAt: string;
  providers: Array<{
    provider: string;
    model: string;
    status: 'observed' | 'blocked';
    blocker: string | null;
    cases: Array<{
      case: string;
      requestId: string | null;
      cacheSettingObservedOnWire: boolean;
      usage: {
        inputTokens: number;
        cachedTokens: number;
        cacheCreationTokens: number;
        cacheWriteTokens: number;
      };
    }>;
  }>;
};

const emptySeed: ChatSeed = {
  key: `draft-${crypto.randomUUID()}`,
  sessionId: null,
  messages: [],
  lastEventId: 0,
};

export function App() {
  const [sessions, setSessions] = useState<PublicSession[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [provider, setProvider] = useState('openai');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [seed, setSeed] = useState<ChatSeed>(emptySeed);
  const [enteredWorkbench, setEnteredWorkbench] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);
  const [authSession, setAuthSession] = useState<AuthSession | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [features, setFeatures] = useState<RuntimeFeatures>({ proofLab: false });
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [customerDeploymentOpen, setCustomerDeploymentOpen] = useState(false);
  const [customerPolicyOpen, setCustomerPolicyOpen] = useState(false);
  const [customerPolicySurface, setCustomerPolicySurface] = useState<'github' | 'apps'>('github');
  const [customerJobsOpen, setCustomerJobsOpen] = useState(false);
  const [customerDeployment, setCustomerDeployment] = useState<CustomerDeploymentStatus | null>(null);
  const [autoOpenDesktopId, setAutoOpenDesktopId] = useState<string | null>(null);
  const [authAttempt, setAuthAttempt] = useState(0);
  const [githubApp, setGithubApp] = useState<GithubAppStatus | null>(null);
  const [slackApp, setSlackApp] = useState<SlackAppStatus | null>(null);
  const [githubNotice, setGithubNotice] = useState<{
    kind: 'success' | 'error';
    message: string;
  } | null>(null);

  const refresh = useCallback(async () => {
    const [nextSessions, providerResponse] = await Promise.all([
      listSessions(),
      api<{
        providers: Provider[];
        features?: RuntimeFeatures;
        githubAppEnabled?: boolean;
        slackAppEnabled?: boolean;
      }>('/api/providers'),
    ]);
    const [nextGithubApp, nextSlackApp] = await Promise.all([
      providerResponse.githubAppEnabled === true
        ? getGithubAppStatus()
        : Promise.resolve({ enabled: false, installations: [] } satisfies GithubAppStatus),
      providerResponse.slackAppEnabled === true
        ? getSlackAppStatus()
        : Promise.resolve({ enabled: false, installations: [] } satisfies SlackAppStatus),
    ]);
    setSessions(nextSessions);
    setProviders(providerResponse.providers);
    setFeatures(providerResponse.features ?? { proofLab: false });
    const preferred = providerResponse.providers.find((item) => item.provider === provider && item.credentialPresent)
      ?? providerResponse.providers.find((item) => item.credentialPresent);
    if (preferred) setProvider(preferred.provider);
    setGithubApp(nextGithubApp);
    setSlackApp(nextSlackApp);
  }, [provider]);

  useEffect(() => {
    let active = true;
    const searchParams = new URLSearchParams(window.location.search);
    const oauthError = searchParams.get('auth_error');
    const githubResult = searchParams.get('github_app');
    const slackResult = searchParams.get('slack_app');
    if (oauthError) {
      setAuthError('Google sign-in could not be completed. Please try again.');
    }
    if (githubResult === 'connected') {
      setGithubNotice({
        kind: 'success',
        message: 'GitHub App connected. Choose repository access to activate it in AWS.',
      });
    } else if (githubResult === 'error') {
      setGithubNotice({
        kind: 'error',
        message: 'GitHub could not be connected. Please try the installation again.',
      });
    }
    if (slackResult === 'connected') {
      setGithubNotice({
        kind: 'success',
        message: 'Slack connected. Mentions and direct messages now receive replies from your VPC.',
      });
    } else if (slackResult === 'error') {
      setGithubNotice({
        kind: 'error',
        message: 'Slack could not be connected. Please try the installation again.',
      });
    }
    if (oauthError || githubResult || slackResult) {
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('auth_error');
      cleanUrl.searchParams.delete('github_app');
      cleanUrl.searchParams.delete('slack_app');
      window.history.replaceState({}, '', `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
    }
    void getAuthSession()
      .then((value) => {
        if (active) {
          setAuthSession(value);
          if (!oauthError) setAuthError(null);
        }
      })
      .catch((error: Error) => {
        if (active) setAuthError(error.message);
      });
    return () => {
      active = false;
    };
  }, [authAttempt]);

  useEffect(() => {
    if (!authSession?.authenticated) return;
    void refresh().catch((error: Error) => setFatalError(error.message));
  }, [authSession?.authenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!authSession?.authenticated) {
      setCustomerDeployment(null);
      return;
    }
    let active = true;
    let timer: number | undefined;
    const poll = async () => {
      try {
        const value = await getCurrentCustomerDeployment();
        if (active) setCustomerDeployment(value);
      } catch {
        // Preserve the last known state through transient control-plane failures.
      }
      if (active) timer = window.setTimeout(poll, 10_000);
    };
    void poll();
    return () => {
      active = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [authSession?.authenticated]);

  const selectSession = useCallback(async (id: string) => {
    setLoadingSession(true);
    try {
      const { messages, lastEventId } = await getMessages(id);
      const selected = sessions.find((session) => session.id === id);
      if (selected) setProvider(selected.inferenceProvider);
      runTransition(() => {
        setSelectedId(id);
        setSeed({ key: id, sessionId: id, messages, lastEventId });
        setEnteredWorkbench(true);
      });
    } catch (error) {
      setFatalError(error instanceof Error ? error.message : String(error));
    } finally {
      setLoadingSession(false);
    }
  }, [sessions]);

  const newTask = useCallback(() => {
    runTransition(() => {
      setSelectedId(null);
      setSeed({ ...emptySeed, key: `draft-${crypto.randomUUID()}` });
      setEnteredWorkbench(false);
    });
  }, []);

  const selected = sessions.find((session) => session.id === selectedId) ?? null;

  useEffect(() => {
    if (!selectedId || !selected || !['creating', 'running'].includes(selected.status)) return;
    const timer = window.setInterval(() => {
      void refresh().catch((error: Error) => setFatalError(error.message));
    }, 1_000);
    return () => window.clearInterval(timer);
  }, [refresh, selected?.status, selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const endSession = useCallback(async (id: string) => {
    setDeletingId(id);
    setSessions((current) => current.map((session) => (
      session.id === id ? { ...session, status: 'ending', workspaceLifecycle: 'terminated', microvmState: 'TERMINATING' } : session
    )));
    try {
      await deleteSession(id);
      setSessions((current) => current.filter((session) => session.id !== id));
      if (selectedId === id) newTask();
    } catch (error) {
      setFatalError(error instanceof Error ? error.message : String(error));
      await refresh();
    } finally {
      setDeletingId(null);
    }
  }, [newTask, refresh, selectedId]);

  return (
    <div className={`app-shell ${enteredWorkbench ? 'workbench-open' : 'home-open'}`}>
      <SessionRail
        sessions={sessions}
        selectedId={selectedId}
        onSelect={selectSession}
        onNew={newTask}
        onConnectAws={() => setCustomerDeploymentOpen(true)}
        onManagePolicy={() => {
          setCustomerPolicySurface('github');
          setCustomerPolicyOpen(true);
        }}
        onManageApps={() => {
          setCustomerPolicySurface('apps');
          setCustomerPolicyOpen(true);
        }}
        onOpenJobs={() => setCustomerJobsOpen(true)}
        customerDeployment={customerDeployment}
        githubApp={githubApp}
        slackApp={slackApp}
        user={authSession?.authenticated ? authSession.user : null}
        onSignOut={() => {
          void signOut()
            .then(() => {
              setGithubApp(null);
              setSlackApp(null);
              setAuthSession({
                authenticated: false,
                googleEnabled: authSession?.googleEnabled ?? false,
                passwordEnabled: authSession?.passwordEnabled ?? false,
                developmentEmailLogin: authSession?.developmentEmailLogin ?? false,
              });
            })
            .catch((error: Error) => setFatalError(error.message));
        }}
        busy={loadingSession}
      />
      <ChatWorkspace
        key={seed.key}
        seed={seed}
        customerDeployment={customerDeployment}
        onOpenCustomerJobs={() => setCustomerJobsOpen(true)}
        selected={selected}
        sessions={sessions}
        providers={providers}
        provider={provider}
        onProvider={setProvider}
        enteredWorkbench={enteredWorkbench}
        proofLabEnabled={features.proofLab}
        onEnterWorkbench={() => setEnteredWorkbench(true)}
        onSessionEstablished={(id) => {
          setAutoOpenDesktopId(null);
          setSelectedId(id);
          setEnteredWorkbench(true);
          void refresh();
        }}
        onRefresh={refresh}
        onEndSession={endSession}
        ending={deletingId === selectedId}
        autoOpenDesktopId={autoOpenDesktopId}
      />
      {customerDeploymentOpen && (
        <CustomerDeploymentDialog
          onClose={() => setCustomerDeploymentOpen(false)}
          onManagePolicy={(installation) => {
            setCustomerDeployment(installation);
            setCustomerDeploymentOpen(false);
            setCustomerPolicySurface('github');
            setCustomerPolicyOpen(true);
          }}
          onOpenJobs={(installation) => {
            setCustomerDeployment(installation);
            setCustomerDeploymentOpen(false);
            setCustomerJobsOpen(true);
          }}
        />
      )}
      {customerJobsOpen && customerDeployment?.status === 'connected' && (
        <CustomerJobsDialog
          installation={customerDeployment}
          onClose={() => setCustomerJobsOpen(false)}
        />
      )}
      {customerPolicyOpen && customerDeployment?.status === 'connected' && (
        <CustomerPolicyDialog
          installation={customerDeployment}
          initialSurface={customerPolicySurface}
          onClose={() => setCustomerPolicyOpen(false)}
          onManageInfrastructure={() => {
            setCustomerPolicyOpen(false);
            setCustomerDeploymentOpen(true);
          }}
        />
      )}
      {authSession && !authSession.authenticated && (
        <SignInGate
          session={authSession}
          error={authError}
          onAuthenticated={(next) => {
            setAuthError(null);
            setAuthSession(next);
          }}
          onError={setAuthError}
        />
      )}
      {!authSession && !authError && (
        <div className="auth-loading" role="status">
          <Spinner />
          <span>Checking your Cyrus session…</span>
        </div>
      )}
      {!authSession && authError && (
        <div className="auth-backdrop">
          <section className="auth-card" role="alert">
            <span className="auth-kicker">Session unavailable</span>
            <h1>We could not restore your session</h1>
            <p>{authError}</p>
            <button
              type="button"
              className="auth-retry"
              onClick={() => {
                setAuthError(null);
                setAuthAttempt((value) => value + 1);
              }}
            >
              Try again
            </button>
          </section>
        </div>
      )}
      {fatalError && (
        <div className="toast error" role="alert" onClick={() => setFatalError(null)}>
          {fatalError}
        </div>
      )}
      {githubNotice && (
        <div
          className={`toast ${githubNotice.kind}`}
          role={githubNotice.kind === 'error' ? 'alert' : 'status'}
          onClick={() => setGithubNotice(null)}
        >
          {githubNotice.message}
        </div>
      )}
    </div>
  );
}

function SessionRail(props: {
  sessions: PublicSession[];
  selectedId: string | null;
  busy: boolean;
  onSelect: (id: string) => void;
  onNew: () => void;
  onConnectAws: () => void;
  onManagePolicy: () => void;
  onManageApps: () => void;
  onOpenJobs: () => void;
  customerDeployment: CustomerDeploymentStatus | null;
  user: AuthUser | null;
  onSignOut: () => void;
  githubApp: GithubAppStatus | null;
  slackApp: SlackAppStatus | null;
}) {
  const githubInstallations = props.githubApp?.installations ?? [];
  const githubConnected = githubInstallations.length > 0;
  const awsConnected = props.customerDeployment?.status === 'connected';
  const slackInstallation = props.slackApp?.installations.find(
    (item) => item.installationId === props.customerDeployment?.installationId,
  );
  const slackConnected = Boolean(slackInstallation);
  const slackResponsesEnabled = slackInstallation?.responsesEnabled === true;
  return (
    <aside className="session-rail" aria-label="Cyrus sessions">
      <div className="brand-row">
        <div className="cyrus-mark">C</div>
        <div><strong>Cyrus</strong><span>AWS sandbox</span></div>
      </div>
      <button className="new-task" onClick={props.onNew}>＋ New task</button>
      <div className="rail-heading">Recent work</div>
      <nav className="session-list">
        {props.sessions.map((session) => {
          const deleting = session.status === 'ending';
          return (
            <div className={`session-entry ${props.selectedId === session.id ? 'selected' : ''}`} key={session.id}>
              <button
                disabled={props.busy || deleting}
                className="session-row"
                onClick={() => props.onSelect(session.id)}
              >
                <span className={`state-dot ${session.workspaceLifecycle}`} />
                <span className="session-copy">
                  <strong>{session.title}</strong>
                  <small>{session.turnState === 'running' ? 'Working' : workspaceStatus(session.workspaceLifecycle)} · {relativeTime(session.lastActiveAt)}</small>
                </span>
              </button>
            </div>
          );
        })}
        {props.sessions.length === 0 && <div className="rail-empty">Your running work will appear here.</div>}
      </nav>
      <button
        className="connect-aws"
        type="button"
        aria-label={props.customerDeployment ? 'Manage AWS environment' : 'Connect AWS'}
        onClick={props.onConnectAws}
      >
        <span aria-hidden="true">{props.customerDeployment?.status === 'connected' ? '✓' : '↗'}</span>
        <span>
          <strong>{props.customerDeployment ? 'AWS environment' : 'Connect AWS'}</strong>
          <small>{deploymentRailStatus(props.customerDeployment)}</small>
        </span>
      </button>
      {awsConnected && (
        <button
          className="connect-aws agent-jobs"
          type="button"
          aria-label="Open customer VPC agent jobs"
          onClick={props.onOpenJobs}
        >
          <span aria-hidden="true">↯</span>
          <span>
            <strong>Agent sessions</strong>
            <small>{props.customerDeployment?.agentLoopStatus === 'ready'
              ? 'Publish encrypted VPC jobs'
              : 'Worker pool is connecting'}</small>
          </span>
        </button>
      )}
      {awsConnected && (
        <button
          className="connect-aws policy-access app-access"
          type="button"
          aria-label="Manage curated app access"
          onClick={props.onManageApps}
        >
          <span aria-hidden="true">⊞</span>
          <span>
            <strong>App access</strong>
            <small>46 credential-brokered apps</small>
          </span>
        </button>
      )}
      {props.githubApp?.enabled && githubConnected && (
        <button
          className="connect-aws policy-access github-connected"
          type="button"
          aria-label={awsConnected ? 'Manage GitHub access' : 'Continue GitHub setup'}
          onClick={awsConnected ? props.onManagePolicy : props.onConnectAws}
        >
          <span aria-hidden="true">✓</span>
          <span>
            <strong>{awsConnected ? 'GitHub access' : 'GitHub connected'}</strong>
            <small>{githubRailStatus(githubInstallations, props.customerDeployment)}</small>
          </span>
        </button>
      )}
      {props.githubApp?.enabled && !githubConnected && (
        <a className="connect-aws github-connect" href="/auth/github/install">
          <span aria-hidden="true">◈</span>
          <span><strong>Connect GitHub</strong><small>Install repository access</small></span>
        </a>
      )}
      {props.slackApp?.enabled && awsConnected && (
        <a
          className={`connect-aws slack-connect ${slackResponsesEnabled ? 'slack-connected' : ''}`}
          href={`/auth/slack/install?installationId=${encodeURIComponent(props.customerDeployment!.installationId)}`}
          aria-label={slackResponsesEnabled
            ? 'Reconnect Slack triggers'
            : slackConnected
              ? 'Reconnect Slack to enable replies'
              : 'Connect Slack triggers'}
        >
          <span aria-hidden="true">{slackResponsesEnabled ? '✓' : slackConnected ? '!' : '⌁'}</span>
          <span>
            <strong>{slackResponsesEnabled
              ? 'Slack replies'
              : slackConnected
                ? 'Reconnect Slack'
                : 'Connect Slack'}</strong>
            <small>{slackResponsesEnabled
              ? 'Mentions reply from your VPC'
              : slackConnected
                ? 'Enable threaded agent responses'
                : 'Trigger sessions from Slack'}</small>
          </span>
        </a>
      )}
      {props.user && (
        <div className="rail-account">
          <span>{props.user.displayName.slice(0, 1).toUpperCase()}</span>
          <div><strong>{props.user.displayName}</strong><small>{props.user.email}</small></div>
          <button type="button" onClick={props.onSignOut}>Sign out</button>
        </div>
      )}
      <div className="rail-footer"><span className="secure-dot" /> Public web · isolated desktop</div>
    </aside>
  );
}

type ChatWorkspaceProps = {
  seed: ChatSeed;
  selected: PublicSession | null;
  sessions: PublicSession[];
  providers: Provider[];
  provider: string;
  enteredWorkbench: boolean;
  proofLabEnabled: boolean;
  onProvider: (provider: string) => void;
  onEnterWorkbench: () => void;
  onSessionEstablished: (id: string) => void;
  onRefresh: () => Promise<void>;
  onEndSession: (id: string) => Promise<void>;
  ending: boolean;
  autoOpenDesktopId?: string | null;
  customerDeployment: CustomerDeploymentStatus | null;
  onOpenCustomerJobs: () => void;
};

function ChatWorkspace(props: ChatWorkspaceProps) {
  if (props.customerDeployment?.status === 'connected') {
    return (
      <CustomerVpcWorkspace
        installation={props.customerDeployment}
        enteredWorkbench={props.enteredWorkbench}
        sessions={props.sessions}
        onEnterWorkbench={props.onEnterWorkbench}
        onOpenCustomerJobs={props.onOpenCustomerJobs}
      />
    );
  }
  return <LegacyChatWorkspace {...props} />;
}

type CustomerThreadJob = {
  job: CustomerJob;
  prompt: string | null;
  events: DecryptedEvent[];
};

type CustomerConversationEntry = {
  id: string;
  role: 'user' | AgentOutputEntry['role'];
  label: string;
  text: string;
  createdAt: string;
};

type CustomerSurfaceLease = CustomerSurfaceConnection & {
  accessId: string;
  sessionId: string;
};

const activeCustomerJobStatuses = new Set<CustomerJob['status']>([
  'queued',
  'leased',
  'running',
  'retry_wait',
  'cancelling',
]);

function CustomerVpcWorkspace({
  installation,
  enteredWorkbench,
  sessions,
  onEnterWorkbench,
  onOpenCustomerJobs,
}: {
  installation: CustomerDeploymentStatus;
  enteredWorkbench: boolean;
  sessions: PublicSession[];
  onEnterWorkbench: () => void;
  onOpenCustomerJobs: () => void;
}) {
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [title, setTitle] = useState('Customer VPC session');
  const [threadJobs, setThreadJobs] = useState<CustomerThreadJob[]>([]);
  const [publishing, setPublishing] = useState(false);
  const [surfaceNotice, setSurfaceNotice] = useState<string | null>(null);
  const [surfaceLease, setSurfaceLease] = useState<CustomerSurfaceLease | null>(null);
  const [surfaceOpening, setSurfaceOpening] = useState(false);
  const [surfaceReset, setSurfaceReset] = useState(0);
  const surfaceLeaseRef = useRef<CustomerSurfaceLease | null>(null);
  const surfaceOpenPromise = useRef<Promise<CustomerSurfaceLease> | null>(null);
  const eventCursors = useRef(new Map<string, number>());
  const jobIds = useMemo(
    () => threadJobs.map(({ job }) => job.jobId).join(','),
    [threadJobs],
  );

  useEffect(() => {
    if (!jobIds) return;
    const ids = jobIds.split(',');
    let current = true;
    let timer: number | undefined;
    const poll = async () => {
      try {
        const [allJobs, eventBatches] = await Promise.all([
          listCustomerJobs(installation.installationId),
          Promise.all(ids.map(async (jobId) => {
            const after = eventCursors.current.get(jobId) ?? 0;
            const events = await getCustomerJobEvents(jobId, after);
            const decrypted = await Promise.all(events.map(decryptEvent));
            if (events.length > 0) {
              eventCursors.current.set(
                jobId,
                Math.max(after, ...events.map((event) => event.id)),
              );
            }
            return { jobId, decrypted };
          })),
        ]);
        if (current) {
          const jobsById = new Map(allJobs.map((job) => [job.jobId, job]));
          const eventsByJob = new Map(eventBatches.map((batch) => [
            batch.jobId,
            batch.decrypted,
          ]));
          setThreadJobs((existing) => existing.map((item) => ({
            ...item,
            job: jobsById.get(item.job.jobId) ?? item.job,
            events: mergeEvents(
              item.events,
              eventsByJob.get(item.job.jobId) ?? [],
            ),
          })));
          setSurfaceNotice(null);
        }
      } catch (error) {
        if (current) {
          setSurfaceNotice(error instanceof Error ? error.message : String(error));
        }
      }
      if (current) timer = window.setTimeout(poll, 1_500);
    };
    void poll();
    return () => {
      current = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [installation.installationId, jobIds]);

  const activeJob = [...threadJobs]
    .reverse()
    .find(({ job }) => activeCustomerJobStatuses.has(job.status))?.job ?? null;
  const workerReady = installation.agentLoopStatus === 'ready';
  const conversation = useMemo(
    () => customerConversation(threadJobs),
    [threadJobs],
  );

  useEffect(() => {
    surfaceLeaseRef.current = surfaceLease;
  }, [surfaceLease]);

  const closeSurfaces = useCallback(async (reset = true) => {
    let lease = surfaceLeaseRef.current;
    if (!lease && surfaceOpenPromise.current) {
      lease = await surfaceOpenPromise.current.catch(() => null);
    }
    if (!lease) {
      if (reset) setSurfaceReset((value) => value + 1);
      return;
    }
    surfaceLeaseRef.current = null;
    setSurfaceLease(null);
    if (reset) setSurfaceReset((value) => value + 1);
    await closeCustomerSurface(
      lease.accessId,
      installation.installationId,
      lease.sessionId,
    );
  }, [installation.installationId]);

  const ensureSurfaceAccess = useCallback(async (): Promise<CustomerSurfaceLease> => {
    const current = surfaceLeaseRef.current;
    if (
      current
      && new Date(current.credentialExpiresAt).getTime() > Date.now() + 60_000
    ) {
      return current;
    }
    if (current) await closeSurfaces(false);
    if (surfaceOpenPromise.current) return surfaceOpenPromise.current;
    if (!sessionId) throw new Error('Send the first task before opening workspace surfaces.');
    if (activeJob || publishing) {
      throw new Error('Wait for the active agent turn to finish before opening workspace surfaces.');
    }
    setSurfaceOpening(true);
    setSurfaceNotice(null);
    const opening = (async () => {
      const encryption = await getCustomerJobEncryption(installation.installationId);
      const encrypted = await encryptCustomerJobPayload(encryption, {
        purpose: 'workspace_surfaces',
        requestedAt: new Date().toISOString(),
      });
      try {
        const response = await openCustomerSurface({
          installationId: installation.installationId,
          sessionId,
          encryptedRequest: encrypted.envelope,
        });
        const decrypted = await decryptCustomerPayload(
          encrypted.contentKey,
          response.encryptedAccess,
        );
        const connection = parseCustomerSurfaceConnection(decrypted);
        const lease = {
          ...connection,
          accessId: response.accessId,
          sessionId,
        };
        surfaceLeaseRef.current = lease;
        setSurfaceLease(lease);
        return lease;
      } finally {
        encrypted.contentKey.fill(0);
      }
    })();
    surfaceOpenPromise.current = opening;
    try {
      return await opening;
    } finally {
      if (surfaceOpenPromise.current === opening) surfaceOpenPromise.current = null;
      setSurfaceOpening(false);
    }
  }, [
    activeJob,
    closeSurfaces,
    installation.installationId,
    publishing,
    sessionId,
  ]);

  useEffect(() => {
    if (!surfaceLease) return;
    const keepalive = window.setInterval(() => {
      void keepCustomerSurfaceAlive(
        surfaceLease.accessId,
        installation.installationId,
        surfaceLease.sessionId,
      ).catch((error) => {
        if (surfaceLeaseRef.current?.accessId !== surfaceLease.accessId) return;
        surfaceLeaseRef.current = null;
        setSurfaceLease(null);
        setSurfaceReset((value) => value + 1);
        setSurfaceNotice(error instanceof Error ? error.message : String(error));
      });
    }, 20_000);
    return () => window.clearInterval(keepalive);
  }, [installation.installationId, surfaceLease]);

  useEffect(() => () => {
    const lease = surfaceLeaseRef.current;
    if (!lease) return;
    surfaceLeaseRef.current = null;
    void closeCustomerSurface(
      lease.accessId,
      installation.installationId,
      lease.sessionId,
    ).catch(() => undefined);
  }, [installation.installationId]);

  const publish = async (event: FormEvent) => {
    event.preventDefault();
    const prompt = input.trim();
    if (!prompt || publishing || activeJob || surfaceOpening || !workerReady) return;
    setPublishing(true);
    setSurfaceNotice(null);
    let contentKey: Uint8Array | null = null;
    try {
      if (surfaceLeaseRef.current || surfaceOpenPromise.current) {
        await closeSurfaces();
      }
      const jobType = sessionId ? 'session.prompt' : 'session.start';
      const nextTitle = sessionId ? title : titleFromCustomerPrompt(prompt);
      const encryption = await getCustomerJobEncryption(installation.installationId);
      const encrypted = await encryptCustomerJobPayload(
        encryption,
        jobType === 'session.start'
          ? { title: nextTitle, prompt }
          : { prompt },
      );
      contentKey = encrypted.contentKey;
      const job = await createCustomerJob({
        installationId: installation.installationId,
        jobType,
        ...(sessionId ? { sessionId } : {}),
        idempotencyKey: crypto.randomUUID(),
        payload: encrypted.envelope,
      });
      rememberCustomerJobKey(job.jobId, contentKey);
      setSessionId(job.sessionId);
      setTitle(nextTitle);
      setThreadJobs((current) => [...current, { job, prompt, events: [] }]);
      setInput('');
      onEnterWorkbench();
    } catch (error) {
      setSurfaceNotice(error instanceof Error ? error.message : String(error));
    } finally {
      contentKey?.fill(0);
      setPublishing(false);
    }
  };

  const stop = async () => {
    if (!activeJob || publishing) return;
    setPublishing(true);
    setSurfaceNotice(null);
    try {
      const cancelled = await cancelCustomerJob(activeJob.jobId);
      setThreadJobs((current) => current.map((item) => (
        item.job.jobId === cancelled.jobId ? { ...item, job: cancelled } : item
      )));
      setSurfaceNotice('The active turn is being stopped. This session remains available.');
    } catch (error) {
      setSurfaceNotice(error instanceof Error ? error.message : String(error));
    } finally {
      setPublishing(false);
    }
  };

  const turnActive = Boolean(activeJob) || publishing || surfaceOpening;
  const turnLabel = publishing && !activeJob
    ? 'Encrypting'
    : surfaceOpening
      ? 'Restoring workspace'
    : customerJobStatusLabel(activeJob?.status);

  return (
    <main className="workspace-stage customer-vpc-workspace">
      {!enteredWorkbench && (
        <section className="home-copy">
          <div className="home-kicker">CYRUS · CUSTOMER VPC</div>
          <h1>What do you want to get done?</h1>
          <p>Your request is encrypted here and runs inside your isolated AWS environment.</p>
        </section>
      )}

      {enteredWorkbench && (
        <section className="workbench">
          <header className="workbench-header customer-vpc-header">
            <div className="workbench-title">
              <span className="eyebrow">Encrypted agent session</span>
              <h1>{title}</h1>
            </div>
            <div className="runtime-strip">
              <span className={`state-dot ${workerReady ? 'ready' : 'failed'}`} />
              <span><small>Runtime</small>Customer VPC</span>
              <span><small>Session</small>{sessionId ? sessionId.slice(0, 8) : 'Starting'}</span>
            </div>
            <button
              type="button"
              className="vpc-ledger-link"
              onClick={onOpenCustomerJobs}
            >
              Job ledger
            </button>
          </header>
          <div className="workbench-grid customer-vpc-grid">
            <CustomerLiveSurfaces
              key={surfaceReset}
              installation={installation}
              sessionId={sessionId}
              activeJob={activeJob}
              opening={surfaceOpening}
              ensureAccess={ensureSurfaceAccess}
            />
            <section className="chat-panel" aria-label="Agent chat">
              <div className="chat-heading">
                <div><span className="eyebrow">Agent thread</span><strong>Live VPC output</strong></div>
                <span className={`stream-state ${turnActive ? 'active' : 'ready'}`}>
                  <span className="stream-signal" aria-hidden="true"><i /><i /><i /></span>
                  {turnLabel}
                </span>
              </div>
              <CustomerMessageFeed
                entries={conversation}
                activeLabel={turnActive ? customerProgressLabel(activeJob?.status) : null}
              />
              {surfaceNotice && <div className="inline-notice" role="status">{surfaceNotice}</div>}
            </section>
          </div>
        </section>
      )}

      {!enteredWorkbench && <RecentWork sessions={sessions} />}

      <form className="shared-composer" onSubmit={publish}>
        <textarea
          aria-label="Task message"
          placeholder={sessionId
            ? 'Send the next instruction to this VPC session…'
            : 'Ask Cyrus to start an agent session in your VPC…'}
          value={input}
          disabled={!workerReady}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              event.currentTarget.form?.requestSubmit();
            }
          }}
          rows={enteredWorkbench ? 2 : 4}
        />
        <div className="composer-footer">
          <div className="vpc-route">
            <span aria-hidden="true">◆</span>
            <div>
              <strong>Customer VPC · OpenAI</strong>
              <small>{workerReady ? 'Encrypted end to end' : customerWorkerStatus(installation)}</small>
            </div>
          </div>
          {activeJob ? (
            <button
              type="button"
              className="stop-composer"
              onClick={() => void stop()}
              disabled={publishing}
              aria-label="Stop agent"
            >
              ■ Stop
            </button>
          ) : (
            <button
              type="submit"
              className="send-button"
              disabled={!input.trim() || publishing || surfaceOpening || !workerReady}
              aria-label="Send task"
            >
              ↵
            </button>
          )}
        </div>
        {!workerReady && (
          <div className="vpc-composer-warning" role="status">
            {customerWorkerStatus(installation)}
          </div>
        )}
      </form>
    </main>
  );
}

function CustomerLiveSurfaces({
  installation,
  sessionId,
  activeJob,
  opening,
  ensureAccess,
}: {
  installation: CustomerDeploymentStatus;
  sessionId: string | null;
  activeJob: CustomerJob | null;
  opening: boolean;
  ensureAccess: () => Promise<CustomerSurfaceLease>;
}) {
  const [activeSurface, setActiveSurface] = useState<'none' | 'desktop' | 'terminal'>('none');
  const [opened, setOpened] = useState({ desktop: false, terminal: false });
  const [desktopStatus, setDesktopStatus] = useState<SurfaceStatus>('unopened');
  const [terminalStatus, setTerminalStatus] = useState<SurfaceStatus>('unopened');
  const disabledReason = !sessionId
    ? 'Available after the first task'
    : activeJob
      ? 'Available after this turn'
      : null;
  const open = (surface: 'desktop' | 'terminal') => {
    if (disabledReason) return;
    setOpened((current) => ({ ...current, [surface]: true }));
    setActiveSurface(surface);
  };

  return (
    <section className="live-surfaces customer-live-surfaces" aria-label="Live customer workspace surfaces">
      <div className="surface-tabs" role="tablist" aria-label="Workspace view">
        <button
          type="button"
          role="tab"
          aria-selected={activeSurface === 'desktop'}
          disabled={Boolean(disabledReason)}
          title={disabledReason ?? 'Open the live VPC desktop'}
          onClick={() => open('desktop')}
        >
          Desktop <span>{disabledReason ? 'waiting' : desktopStatus}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeSurface === 'terminal'}
          disabled={Boolean(disabledReason)}
          title={disabledReason ?? 'Open a terminal in the VPC workspace'}
          onClick={() => open('terminal')}
        >
          Terminal <span>{disabledReason ? 'waiting' : terminalStatus}</span>
        </button>
        <div className="surface-boundary">
          <i aria-hidden="true" />
          Customer VPC
        </div>
      </div>
      <div className="surface-stack">
        {activeSurface === 'none' && (
          <div className="surface-empty customer-surface-empty">
            <span className="eyebrow">Customer-owned workspace</span>
            <strong>{disabledReason ?? 'Desktop and Terminal are ready on demand'}</strong>
            <span>
              {sessionId
                ? 'Opening a surface restores the latest encrypted snapshot. Leaving it checkpoints and releases the MicroVM.'
                : 'Complete the first agent turn to create a restorable workspace.'}
            </span>
            <dl>
              <div><dt>Region</dt><dd>{installation.region}</dd></div>
              <div><dt>Workers</dt><dd>{installation.agentLoopConnectedWorkers ?? 0}</dd></div>
              <div><dt>Capacity</dt><dd>{installation.agentLoopAvailableCapacity ?? 0}</dd></div>
            </dl>
          </div>
        )}
        {opened.desktop && (
          <div
            className={`surface-pane ${activeSurface === 'desktop' ? 'active' : ''}`}
            role="tabpanel"
            aria-label="Desktop panel"
            hidden={activeSurface !== 'desktop'}
          >
            <CustomerDesktopSurface
              ensureAccess={ensureAccess}
              opening={opening}
              onConnectionStatus={setDesktopStatus}
            />
          </div>
        )}
        {opened.terminal && (
          <div
            className={`surface-pane ${activeSurface === 'terminal' ? 'active' : ''}`}
            role="tabpanel"
            aria-label="Terminal panel"
            hidden={activeSurface !== 'terminal'}
          >
            <CustomerTerminalSurface
              ensureAccess={ensureAccess}
              onConnectionStatus={setTerminalStatus}
            />
          </div>
        )}
      </div>
    </section>
  );
}

function CustomerDesktopSurface({
  ensureAccess,
  opening,
  onConnectionStatus,
}: {
  ensureAccess: () => Promise<CustomerSurfaceLease>;
  opening: boolean;
  onConnectionStatus: (status: SurfaceStatus) => void;
}) {
  const host = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<'opening' | 'ready' | 'error'>('opening');
  const [diagnostic, setDiagnostic] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!host.current) return;
    let disposed = false;
    let rfb: RFB | null = null;
    setStatus('opening');
    setDiagnostic(null);
    onConnectionStatus('opening');
    void Promise.all([ensureAccess(), import('@novnc/novnc')])
      .then(([access, noVnc]) => {
        if (disposed || !host.current) return;
        rfb = new noVnc.default(host.current, access.desktop.url, {
          shared: true,
          wsProtocols: access.desktop.protocols,
        });
        rfb.scaleViewport = true;
        rfb.resizeSession = false;
        rfb.addEventListener('connect', () => {
          if (disposed) return;
          setStatus('ready');
          onConnectionStatus('ready');
          rfb?.focus({ preventScroll: true });
        });
        rfb.addEventListener('disconnect', ((event: RfbDisconnectEvent) => {
          if (disposed || event.detail.clean) return;
          setDiagnostic('The VPC desktop connection closed unexpectedly.');
          setStatus('error');
          onConnectionStatus('error');
        }) as EventListener);
        rfb.addEventListener('securityfailure', () => {
          if (disposed) return;
          setDiagnostic('The short-lived desktop credential was rejected.');
          setStatus('error');
          onConnectionStatus('error');
        });
      })
      .catch((error) => {
        if (disposed) return;
        setDiagnostic(error instanceof Error ? error.message : String(error));
        setStatus('error');
        onConnectionStatus('error');
      });
    return () => {
      disposed = true;
      onConnectionStatus('unopened');
      rfb?.disconnect();
      host.current?.replaceChildren();
    };
  }, [attempt]);

  return (
    <section className="surface-card desktop-card customer-desktop-card">
      <div className="surface-body">
        <div
          ref={host}
          className={`customer-desktop-host ${status === 'ready' ? 'surface-ready' : 'surface-hidden'}`}
        />
        {status === 'opening' && (
          <div className="surface-placeholder">
            <Spinner />
            <strong>{opening ? 'Restoring your workspace…' : 'Opening Desktop…'}</strong>
            <span>Restoring a terminated session can take up to two minutes.</span>
          </div>
        )}
        {status === 'error' && (
          <SurfaceFailure
            title="Couldn’t open Desktop"
            diagnostic={diagnostic}
            onRetry={() => setAttempt((value) => value + 1)}
          />
        )}
      </div>
    </section>
  );
}

function CustomerTerminalSurface({
  ensureAccess,
  onConnectionStatus,
}: {
  ensureAccess: () => Promise<CustomerSurfaceLease>;
  onConnectionStatus: (status: SurfaceStatus) => void;
}) {
  const host = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<'opening' | 'ready' | 'error'>('opening');
  const [diagnostic, setDiagnostic] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!host.current) return;
    let socket: WebSocket | null = null;
    let disposed = false;
    const terminal = new Terminal({
      cursorBlink: true,
      fontSize: 12,
      lineHeight: 1.25,
      fontFamily: '"SFMono-Regular", Consolas, monospace',
      theme: { background: '#111418', foreground: '#d8dde3', cursor: '#d8ff74' },
    });
    const fit = new FitAddon();
    terminal.loadAddon(fit);
    terminal.open(host.current);
    setStatus('opening');
    setDiagnostic(null);
    onConnectionStatus('opening');
    void ensureAccess()
      .then((access) => {
        if (disposed) return;
        const next = new WebSocket(access.terminal.url, access.terminal.protocols);
        socket = next;
        next.onopen = () => {
          if (disposed) return;
          fit.fit();
          next.send(JSON.stringify({
            type: 'resize',
            cols: terminal.cols,
            rows: terminal.rows,
          }));
          setStatus('ready');
          onConnectionStatus('ready');
          terminal.focus();
        };
        next.onmessage = (event) => terminal.write(String(event.data));
        next.onclose = () => {
          if (disposed) return;
          setDiagnostic('The VPC terminal connection closed.');
          setStatus('error');
          onConnectionStatus('error');
        };
        next.onerror = () => next.close();
      })
      .catch((error) => {
        if (disposed) return;
        setDiagnostic(error instanceof Error ? error.message : String(error));
        setStatus('error');
        onConnectionStatus('error');
      });
    const data = terminal.onData((value) => {
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'input', data: value }));
      }
    });
    const resize = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        if (disposed) return;
        fit.fit();
        if (socket?.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({
            type: 'resize',
            cols: terminal.cols,
            rows: terminal.rows,
          }));
        }
      });
    });
    resize.observe(host.current);
    return () => {
      disposed = true;
      onConnectionStatus('unopened');
      resize.disconnect();
      data.dispose();
      socket?.close();
      terminal.dispose();
    };
  }, [attempt]);

  return (
    <section className="surface-card terminal-card">
      <div
        ref={host}
        className={`terminal-host ${status === 'ready' ? 'surface-ready' : 'surface-hidden'}`}
      />
      {status === 'opening' && (
        <div className="surface-placeholder">
          <Spinner />
          <strong>Opening Terminal…</strong>
          <span>Restoring a terminated session can take up to two minutes.</span>
        </div>
      )}
      {status === 'error' && (
        <SurfaceFailure
          title="Couldn’t open Terminal"
          diagnostic={diagnostic}
          onRetry={() => setAttempt((value) => value + 1)}
        />
      )}
    </section>
  );
}

function parseCustomerSurfaceConnection(value: unknown): CustomerSurfaceConnection {
  if (
    typeof value !== 'object'
    || value === null
    || (value as { version?: unknown }).version !== 1
  ) {
    throw new Error('The VPC worker returned malformed workspace access.');
  }
  const candidate = value as Partial<CustomerSurfaceConnection>;
  for (const target of [candidate.desktop, candidate.terminal]) {
    if (
      !target
      || typeof target.url !== 'string'
      || !target.url.startsWith('wss://')
      || !Array.isArray(target.protocols)
      || target.protocols.length !== 3
      || target.protocols.some((protocol) => typeof protocol !== 'string')
    ) {
      throw new Error('The VPC worker returned malformed workspace access.');
    }
  }
  if (
    typeof candidate.credentialExpiresAt !== 'string'
    || !Number.isFinite(new Date(candidate.credentialExpiresAt).getTime())
  ) {
    throw new Error('The VPC worker returned malformed workspace access.');
  }
  return candidate as CustomerSurfaceConnection;
}

function CustomerVpcRuntime({
  installation,
  sessionId,
  activeJob,
}: {
  installation: CustomerDeploymentStatus;
  sessionId: string | null;
  activeJob: CustomerJob | null;
}) {
  return (
    <section className="customer-vpc-runtime" aria-label="Customer VPC runtime">
      <header>
        <span className="eyebrow">Execution boundary</span>
        <strong>Your AWS account</strong>
        <p>The VPC worker owns the complete MicroVM lifecycle. Cyrus carries only encrypted jobs and output.</p>
      </header>
      <div className="vpc-runtime-route" role="group" aria-label="Encrypted task route">
        <div><i />Browser encryption</div>
        <span aria-hidden="true">→</span>
        <div><i />VPC worker</div>
        <span aria-hidden="true">→</span>
        <div><i />Isolated MicroVM</div>
      </div>
      <dl>
        <div><dt>Region</dt><dd>{installation.region}</dd></div>
        <div><dt>Workers</dt><dd>{installation.agentLoopConnectedWorkers ?? 0} connected</dd></div>
        <div><dt>Capacity</dt><dd>{installation.agentLoopAvailableCapacity ?? 0} available</dd></div>
        <div><dt>Session</dt><dd>{sessionId ? sessionId.slice(0, 8) : 'Pending first task'}</dd></div>
      </dl>
      <div className={`vpc-runtime-state ${activeJob ? 'active' : 'ready'}`}>
        <span className="turn-pulse" aria-hidden="true" />
        <div>
          <strong>{activeJob ? customerProgressLabel(activeJob.status) : 'Ready for a task'}</strong>
          <small>{activeJob
            ? 'Output is returning as encrypted events.'
            : 'Each new task gets an isolated, customer-owned MicroVM.'}</small>
        </div>
      </div>
      <footer><span aria-hidden="true">◆</span> Cyrus cannot read your task or agent transcript.</footer>
    </section>
  );
}

function CustomerMessageFeed({
  entries,
  activeLabel,
}: {
  entries: CustomerConversationEntry[];
  activeLabel: string | null;
}) {
  const feed = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    feed.current?.scrollTo({ top: feed.current.scrollHeight, behavior: 'smooth' });
  }, [entries, activeLabel]);
  return (
    <div
      className="message-feed customer-message-feed"
      ref={feed}
      role="log"
      aria-live="polite"
      tabIndex={0}
    >
      {entries.map((entry) => {
        if (entry.role === 'user' || entry.role === 'assistant') {
          return (
            <article className={`message ${entry.role}`} key={entry.id}>
              <div className="message-role">{entry.label}</div>
              <div className="message-parts"><p>{entry.text}</p></div>
            </article>
          );
        }
        return (
          <div className={`vpc-thread-event ${entry.role}`} key={entry.id}>
            <span>{entry.label}</span>
            <p>{entry.text}</p>
          </div>
        );
      })}
      {entries.length === 0 && (
        <div className="chat-empty">Your prompt and decrypted agent output will appear here.</div>
      )}
      {activeLabel && <TurnProgress label={activeLabel} />}
    </div>
  );
}

function customerConversation(threadJobs: CustomerThreadJob[]): CustomerConversationEntry[] {
  return threadJobs.flatMap(({ job, prompt, events }) => {
    const entries: CustomerConversationEntry[] = [];
    if (prompt) {
      entries.push({
        id: `${job.jobId}:prompt`,
        role: 'user',
        label: 'You',
        text: prompt,
        createdAt: job.createdAt,
      });
    }
    for (const output of coalesceOutput(events)) {
      if (output.role === 'user' || output.role === 'status') continue;
      entries.push({
        id: `${job.jobId}:${output.id}`,
        role: output.role,
        label: output.role === 'assistant' ? 'Cyrus' : output.label,
        text: output.text,
        createdAt: output.createdAt,
      });
    }
    return entries;
  });
}

function titleFromCustomerPrompt(prompt: string): string {
  const firstLine = prompt.split(/\r?\n/u)[0]!.trim();
  return (firstLine || 'Customer VPC session').slice(0, 72);
}

function customerJobStatusLabel(status: CustomerJob['status'] | undefined): string {
  if (!status) return 'Ready';
  if (status === 'queued') return 'Queued';
  if (status === 'leased') return 'Assigned';
  if (status === 'running') return 'Working';
  if (status === 'retry_wait') return 'Retrying';
  if (status === 'cancelling') return 'Stopping';
  return status.replace('_', ' ');
}

function customerProgressLabel(status: CustomerJob['status'] | undefined): string {
  if (status === 'queued') return 'Waiting for a VPC worker';
  if (status === 'leased') return 'Preparing the customer MicroVM';
  if (status === 'retry_wait') return 'Waiting to retry automatically';
  if (status === 'cancelling') return 'Stopping the active turn';
  return 'Cyrus is working in your VPC';
}

function customerWorkerStatus(installation: CustomerDeploymentStatus): string {
  if (installation.agentLoopStatus === 'needs_openai_key') {
    return 'Add the OpenAI API key to your customer stack to start sessions.';
  }
  return 'The customer VPC worker pool is still connecting.';
}

function LegacyChatWorkspace(props: ChatWorkspaceProps) {
  const [input, setInput] = useState('');
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [surfaceNotice, setSurfaceNotice] = useState<string | null>(null);
  const [cacheProof, setCacheProof] = useState<PromptCacheProof | null>(null);
  const providerRef = useRef(props.provider);
  providerRef.current = props.provider;
  const refreshTimer = useRef<number | null>(null);

  const scheduleRefresh = useCallback(() => {
    if (refreshTimer.current !== null) return;
    refreshTimer.current = window.setTimeout(() => {
      refreshTimer.current = null;
      void props.onRefresh();
    }, 160);
  }, [props.onRefresh]);

  useEffect(() => () => {
    if (refreshTimer.current !== null) window.clearTimeout(refreshTimer.current);
  }, []);

  const transport = useMemo(() => new CyrusChatTransport({
    sessionId: props.seed.sessionId,
    initialEventId: props.seed.lastEventId,
    provider: () => providerRef.current,
    headers: sessionHeaders,
    onSession: props.onSessionEstablished,
    onChunk: (chunk: UIMessageChunk) => {
      if (chunk.type === 'data-cyrus-event') scheduleRefresh();
    },
  }), []); // the keyed workspace owns one transport lifecycle

  const chat = useChat({
    id: props.seed.key,
    messages: props.seed.messages,
    transport,
    resume: Boolean(props.seed.sessionId && props.selected?.turnState === 'running'),
    throttle: 24,
    onData: () => scheduleRefresh(),
    onError: (error) => setSurfaceNotice(error.message),
    onFinish: () => scheduleRefresh(),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const text = input.trim();
    if (!text || chat.status === 'submitted' || chat.status === 'streaming') return;
    setInput('');
    props.onEnterWorkbench();
    void chat.sendMessage({ text });
  };

  const session = props.sessions.find((item) => item.id === transport.currentSessionId())
    ?? props.selected;
  const activeId = transport.currentSessionId() ?? session?.id ?? null;
  const turnActive = chat.status === 'submitted'
    || chat.status === 'streaming'
    || session?.turnState === 'running'
    || session?.turnState === 'interrupting';
  const turnLabel = session?.turnState === 'interrupting'
    ? 'Stopping'
    : session?.workspaceLifecycle === 'starting'
      ? 'Starting'
      : turnActive
        ? 'Working'
        : 'Ready';

  const action = async (name: string, body?: unknown) => {
    if (!activeId) return;
    setActionBusy(name);
    setSurfaceNotice(null);
    try {
      const result = await sessionAction<{ session: PublicSession; proof?: PromptCacheProof }>(activeId, name, body);
      await props.onRefresh();
      if (result.proof) setCacheProof(result.proof);
      if (name === 'proof/computer-only' || (name === 'restart-task' && result.session.status === 'running')) {
        void chat.resumeStream();
      }
    } catch (error) {
      setSurfaceNotice(error instanceof Error ? error.message : String(error));
    } finally {
      setActionBusy(null);
    }
  };

  const stop = async () => {
    chat.stop();
    setActionBusy('stop');
    try {
      await transport.interrupt();
      if (activeId) {
        const persisted = await getMessages(activeId);
        chat.setMessages(persisted.messages);
      }
      setSurfaceNotice('Turn stopped. Your workspace is ready for another message.');
      await props.onRefresh();
    } finally {
      setActionBusy(null);
    }
  };

  return (
    <main className="workspace-stage">
      {!props.enteredWorkbench && (
        <section className="home-copy">
          <div className="home-kicker">CYRUS · SECURE WORKSPACE</div>
          <h1>What do you want to get done?</h1>
          <p>One request opens an isolated AWS desktop and starts the agent.</p>
        </section>
      )}

      {props.enteredWorkbench && (
        <section className="workbench">
          <WorkbenchHeader
            session={session}
            busy={actionBusy}
            proofLabEnabled={props.proofLabEnabled}
            onAction={action}
            onEndSession={() => activeId ? props.onEndSession(activeId) : Promise.resolve()}
            ending={props.ending}
          />
          <div className="workbench-grid">
            <LiveSurfaces
              session={session}
              autoOpenDesktop={Boolean(
                props.autoOpenDesktopId && props.autoOpenDesktopId === session?.id,
              )}
            />
            <section className="chat-panel" aria-label="Agent chat">
              <div className="chat-heading">
                <div><span className="eyebrow">Agent thread</span><strong>Live work</strong></div>
                <span className={`stream-state ${turnActive ? 'active' : 'ready'}`}>
                  <span className="stream-signal" aria-hidden="true"><i /><i /><i /></span>
                  {turnLabel}
                </span>
              </div>
              <MessageFeed
                messages={chat.messages}
                activeLabel={turnActive ? (turnLabel === 'Starting' ? 'Starting workspace' : 'Cyrus is working') : null}
              />
              {cacheProof && <CacheProofPanel proof={cacheProof} />}
              {surfaceNotice && <div className="inline-notice">{surfaceNotice}</div>}
            </section>
          </div>
        </section>
      )}

      {!props.enteredWorkbench && <RecentWork sessions={props.sessions} />}

      <form className="shared-composer" onSubmit={submit}>
        <textarea
          aria-label="Task message"
          placeholder="Ask Cyrus to work in a secure AWS desktop…"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              event.currentTarget.form?.requestSubmit();
            }
          }}
          rows={props.enteredWorkbench ? 2 : 4}
        />
        <div className="composer-footer">
          <label>
            <span>Model route</span>
            <select value={props.provider} onChange={(event) => props.onProvider(event.target.value)}>
              {props.providers.map((item) => (
                <option key={item.provider} value={item.provider} disabled={!item.credentialPresent}>
                  {item.provider} · {item.model}{item.credentialPresent ? '' : ' · unavailable'}
                </option>
              ))}
            </select>
          </label>
          {chat.status === 'streaming' || chat.status === 'submitted' ? (
            <button type="button" className="stop-composer" onClick={stop} aria-label="Stop agent">■ Stop</button>
          ) : (
            <button type="submit" className="send-button" disabled={!input.trim()} aria-label="Send task">↵</button>
          )}
        </div>
      </form>
    </main>
  );
}

function WorkbenchHeader(props: {
  session: PublicSession | null;
  busy: string | null;
  proofLabEnabled: boolean;
  onAction: (name: string, body?: unknown) => Promise<void>;
  onEndSession: () => Promise<void>;
  ending: boolean;
}) {
  const session = props.session;
  const [confirmEnd, setConfirmEnd] = useState(false);
  return (
    <header className="workbench-header">
      <div className="workbench-title">
        <span className="eyebrow">Workspace</span>
        <h1>{session?.title ?? 'Preparing your workspace'}</h1>
      </div>
      <div className="runtime-strip">
        <span className={`state-dot ${session?.workspaceLifecycle ?? 'starting'}`} />
        <span><small>Workspace</small>{workspaceStatus(session?.workspaceLifecycle ?? 'starting')}</span>
        {session && session.turnState !== 'idle' && <span><small>Turn</small>{turnStatus(session.turnState)}</span>}
      </div>
      <div className="header-actions">
        <details className="more-actions session-actions">
          <summary>Session</summary>
          <div>
            {session?.workspaceLifecycle === 'failed' && (
              <button onClick={() => props.onAction('recover')} disabled={props.busy !== null}>Retry workspace</button>
            )}
            <button className="danger-menu-item" onClick={() => setConfirmEnd(true)} disabled={!session || props.busy !== null || props.ending}>End session…</button>
          </div>
        </details>
        {props.proofLabEnabled && (
          <details className="more-actions proof-actions">
            <summary>Proof lab</summary>
            <div>
              <button onClick={() => props.onAction('proof/computer-only')}>Start computer-only task</button>
              <button onClick={() => props.onAction('proof/long-command')}>Start long command</button>
              <button onClick={() => props.onAction('proof/slow-write')}>Start slow atomic write</button>
              <button onClick={() => props.onAction('proof/cache')}>Run prompt-cache proof</button>
              <button onClick={() => props.onAction('suspend-resume')}>Suspend / resume</button>
            </div>
          </details>
        )}
      </div>
      {confirmEnd && (
        <div className="confirm-backdrop" role="presentation">
          <div className="confirm-card" role="dialog" aria-modal="true" aria-label="End this session?">
            <h2>End this session?</h2>
            <p>This terminates the workspace and its virtual machine. Open Desktop and Terminal connections will close, and this session will be removed.</p>
            <div>
              <button type="button" onClick={() => setConfirmEnd(false)}>Cancel</button>
              <button type="button" className="danger" onClick={() => {
                setConfirmEnd(false);
                void props.onEndSession();
              }}>End session</button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export function LiveSurfaces({
  session,
  autoOpenDesktop = false,
}: {
  session: PublicSession | null;
  autoOpenDesktop?: boolean;
}) {
  const [activeSurface, setActiveSurface] = useState<'none' | 'desktop' | 'terminal'>('none');
  const [opened, setOpened] = useState({ desktop: false, terminal: false });
  const [desktopStatus, setDesktopStatus] = useState<SurfaceStatus>('unopened');
  const [terminalStatus, setTerminalStatus] = useState<SurfaceStatus>('unopened');
  const open = (surface: 'desktop' | 'terminal') => {
    setOpened((current) => ({ ...current, [surface]: true }));
    setActiveSurface(surface);
  };

  useEffect(() => {
    if (!autoOpenDesktop) return;
    setOpened((current) => ({ ...current, desktop: true }));
    setActiveSurface('desktop');
  }, [autoOpenDesktop]);

  return (
    <section className="live-surfaces" aria-label="Live workspace surfaces">
      <div className="surface-tabs" role="tablist" aria-label="Workspace view">
        <button
          type="button"
          role="tab"
          aria-selected={activeSurface === 'desktop'}
          onClick={() => open('desktop')}
        >
          Desktop <span>{desktopStatus}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeSurface === 'terminal'}
          onClick={() => open('terminal')}
        >
          Terminal <span>{terminalStatus}</span>
        </button>
      </div>
      <div className="surface-stack">
        {activeSurface === 'none' && <div className="surface-empty"><strong>Workspace surfaces</strong><span>Open Desktop or Terminal when you need it.</span></div>}
        {opened.desktop && <div className={`surface-pane ${activeSurface === 'desktop' ? 'active' : ''}`} role="tabpanel" aria-label="Desktop panel" hidden={activeSurface !== 'desktop'}><DesktopSurface session={session} onConnectionStatus={setDesktopStatus} /></div>}
        {opened.terminal && <div className={`surface-pane ${activeSurface === 'terminal' ? 'active' : ''}`} role="tabpanel" aria-label="Terminal panel" hidden={activeSurface !== 'terminal'}><TerminalSurface session={session} onConnectionStatus={setTerminalStatus} /></div>}
      </div>
    </section>
  );
}

type SurfaceStatus = 'unopened' | 'opening' | 'ready' | 'error';

function DesktopSurface({ session, onConnectionStatus }: { session: PublicSession | null; onConnectionStatus: (status: SurfaceStatus) => void }) {
  const [source, setSource] = useState<string | null>(null);
  const [status, setStatus] = useState<'opening' | 'ready' | 'error'>('opening');
  const [diagnostic, setDiagnostic] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const frame = useRef<HTMLIFrameElement | null>(null);
  const connectionKey = `${session?.id ?? ''}:${session?.currentMicrovmId ?? ''}:${session?.desktopGeneration ?? 0}:${attempt}`;

  useEffect(() => {
    if (!session?.id) return;
    let cancelled = false;
    setStatus('opening');
    setSource(null);
    setDiagnostic(null);
    onConnectionStatus('opening');
    void sessionAction<{ ticket: string }>(session.id, 'ticket', { scope: 'desktop' })
      .then(({ ticket }) => {
        if (cancelled) return;
        setSource(`/desktop/${session.id}/vnc.html?ticket=${encodeURIComponent(ticket)}&autoconnect=1&resize=scale&path=${encodeURIComponent(`desktop/${session.id}/websockify`)}`);
      })
      .catch((error: Error) => {
        if (cancelled) return;
        setDiagnostic(error.message);
        setStatus('error');
        onConnectionStatus('error');
      });
    return () => { cancelled = true; };
  }, [connectionKey]);

  const onFrameLoad = () => {
    let checks = 0;
    const inspect = () => {
      checks += 1;
      try {
        const document = frame.current?.contentDocument;
        if (document) applyEmbeddedDesktopChrome(document);
        const canvas = document?.querySelector('canvas');
        const noVncStatus = document?.querySelector('#noVNC_status')?.textContent ?? '';
        const rfb = (frame.current?.contentWindow as (Window & { UI?: { rfb?: { _rfbConnectionState?: string } } }) | null)?.UI?.rfb;
        const connected = /connected/iu.test(noVncStatus) && !/disconnected/iu.test(noVncStatus)
          || rfb?._rfbConnectionState === 'connected';
        if (connected && canvas && canvas.width > 0 && canvas.height > 0) {
          setStatus('ready');
          onConnectionStatus('ready');
          attachHumanActivityListeners(frame.current, session?.id ?? null);
          return;
        }
      } catch { /* bounded below */ }
      if (checks < 80) window.setTimeout(inspect, 100);
      else {
        setDiagnostic('The desktop connected but did not produce a usable frame.');
        setStatus('error');
        onConnectionStatus('error');
      }
    };
    inspect();
  };

  return (
    <section className="surface-card desktop-card">
      <div className="surface-body">
        {status === 'opening' && <div className="surface-placeholder"><Spinner /><strong>{restoringCopy(session) ?? 'Opening Desktop…'}</strong></div>}
        {status === 'error' && <SurfaceFailure title="Couldn’t open Desktop" diagnostic={diagnostic} onRetry={() => setAttempt((value) => value + 1)} />}
        {source && <iframe ref={frame} className={status === 'ready' ? 'surface-ready' : 'surface-hidden'} key={connectionKey} src={source} title="Live AWS Desktop" allow="clipboard-read; clipboard-write" onLoad={onFrameLoad} />}
      </div>
    </section>
  );
}

function TerminalSurface({
  session,
  onConnectionStatus,
}: {
  session: PublicSession | null;
  onConnectionStatus: (status: SurfaceStatus) => void;
}) {
  const host = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<'opening' | 'ready' | 'error'>('opening');
  const [diagnostic, setDiagnostic] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const connectionKey = `${session?.id ?? ''}:${session?.currentMicrovmId ?? ''}`;

  useEffect(() => {
    if (!host.current || !session?.id) return;
    setStatus('opening');
    setDiagnostic(null);
    onConnectionStatus('opening');
    const terminal = new Terminal({
      cursorBlink: true,
      fontSize: 12,
      lineHeight: 1.25,
      fontFamily: '"SFMono-Regular", Consolas, monospace',
      theme: { background: '#111418', foreground: '#d8dde3', cursor: '#d8ff74' },
    });
    const fit = new FitAddon();
    terminal.loadAddon(fit);
    terminal.open(host.current);
    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectAttempts = 0;
    let disposed = false;
    const scheduleReconnect = () => {
      if (disposed || reconnectTimer) return;
      reconnectAttempts += 1;
      if (reconnectAttempts > 3) {
        setDiagnostic('The terminal connection did not become usable.');
        setStatus('error');
        onConnectionStatus('error');
        return;
      }
      setStatus('opening');
      onConnectionStatus('opening');
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        void connect();
      }, Math.min(5_000, 750 * 2 ** Math.min(reconnectAttempts - 1, 3)));
    };
    const connect = async () => {
      if (disposed) return;
      onConnectionStatus('opening');
      try {
        const { ticket } = await sessionAction<{ ticket: string }>(session.id, 'ticket', { scope: 'terminal' });
        if (disposed) return;
        const protocol = location.protocol === 'https:' ? 'wss' : 'ws';
        const next = new WebSocket(`${protocol}://${location.host}/ws/terminal/${session.id}?ticket=${encodeURIComponent(ticket)}`);
        socket = next;
        next.onmessage = (event) => {
          const output = String(event.data);
          terminal.write(output);
          if (output.length > 0) {
            reconnectAttempts = 0;
            setStatus('ready');
            onConnectionStatus('ready');
          }
        };
        next.onopen = () => {
          fit.fit();
          next.send(JSON.stringify({ type: 'resize', cols: terminal.cols, rows: terminal.rows }));
        };
        next.onclose = () => {
          if (socket === next) socket = null;
          scheduleReconnect();
        };
        next.onerror = () => next.close();
      } catch (error) {
        setDiagnostic(error instanceof Error ? error.message : String(error));
        setStatus('error');
        onConnectionStatus('error');
      }
    };
    void connect();
    const data = terminal.onData((value) => {
      if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: 'input', data: value }));
    });
    const resize = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        if (disposed) return;
        fit.fit();
        if (socket?.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'resize', cols: terminal.cols, rows: terminal.rows }));
        }
      });
    });
    resize.observe(host.current);
    return () => {
      disposed = true;
      onConnectionStatus('unopened');
      if (reconnectTimer) clearTimeout(reconnectTimer);
      resize.disconnect();
      data.dispose();
      socket?.close();
      terminal.dispose();
    };
  }, [connectionKey, attempt]);

  return (
    <section className="surface-card terminal-card">
      <div ref={host} className={`terminal-host ${status === 'ready' ? 'surface-ready' : 'surface-hidden'}`} />
      {status === 'opening' && <div className="surface-placeholder"><Spinner /><strong>{restoringCopy(session) ?? 'Opening Terminal…'}</strong></div>}
      {status === 'error' && <SurfaceFailure title="Couldn’t open Terminal" diagnostic={diagnostic} onRetry={() => setAttempt((value) => value + 1)} />}
    </section>
  );
}

function SurfaceFailure({ title, diagnostic, onRetry }: { title: string; diagnostic: string | null; onRetry: () => void }) {
  return <div className="surface-placeholder surface-error"><strong>{title}</strong><button type="button" onClick={onRetry}>Retry</button>{diagnostic && <details><summary>Details</summary><p>{diagnostic}</p></details>}</div>;
}

function restoringCopy(session: PublicSession | null): string | null {
  return session?.workspaceLifecycle === 'resuming' || session?.workspaceLifecycle === 'replacing' ? 'Restoring your workspace…' : null;
}

function attachHumanActivityListeners(frame: HTMLIFrameElement | null, sessionId: string | null): void {
  if (!frame?.contentWindow || !sessionId) return;
  let lastSent = 0;
  const note = () => {
    if (Date.now() - lastSent < 500) return;
    lastSent = Date.now();
    void sessionAction(sessionId, 'human-activity').catch(() => undefined);
  };
  frame.contentWindow.addEventListener('pointerdown', note, { capture: true });
  frame.contentWindow.addEventListener('keydown', note, { capture: true });
}

export function applyEmbeddedDesktopChrome(document: Document): void {
  if (document.getElementById('cyrus-embedded-desktop-style')) return;
  const style = document.createElement('style');
  style.id = 'cyrus-embedded-desktop-style';
  style.textContent = `
    #noVNC_control_bar_anchor,
    #noVNC_control_bar,
    #noVNC_status,
    #noVNC_transition,
    #noVNC_connect_dlg,
    #noVNC_password_dlg,
    .noVNC_modal,
    .noVNC_open { display: none !important; }
    html, body, #noVNC_screen, #noVNC_container {
      width: 100% !important;
      height: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      overflow: hidden !important;
      background: #111418 !important;
    }
    #noVNC_screen { display: block !important; }
    #noVNC_canvas { margin: 0 !important; border: 0 !important; }
  `;
  document.head.append(style);
}

export function MessageFeed({ messages, activeLabel = null }: { messages: UIMessage[]; activeLabel?: string | null }) {
  const feed = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    feed.current?.scrollTo({ top: feed.current.scrollHeight, behavior: 'smooth' });
  }, [messages, activeLabel]);
  return (
    <div className="message-feed" ref={feed}>
      {messages.map((message) => {
        const blocks = groupMessageParts(message.parts);
        if (blocks.length === 0) return null;
        return (
          <article className={`message ${message.role}`} key={message.id} data-message-id={message.id}>
            <div className="message-role">{message.role === 'user' ? 'You' : 'Cyrus'}</div>
            <div className="message-parts">
              {blocks.map((block) => {
              if (block.type === 'text') return <p key={block.key}>{block.text}</p>;
              if (block.type === 'tools') return <ToolGroup key={block.key} category={block.category} parts={block.parts} />;
              return null;
              })}
            </div>
          </article>
        );
      })}
      {messages.length === 0 && <div className="chat-empty">Provisioning progress and agent work will stream here.</div>}
      {activeLabel && <TurnProgress label={activeLabel} />}
    </div>
  );
}

function TurnProgress({ label }: { label: string }) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const startedAt = Date.now();
    const timer = window.setInterval(() => setSeconds(Math.floor((Date.now() - startedAt) / 1_000)), 1_000);
    return () => window.clearInterval(timer);
  }, []);
  return (
    <div className="turn-progress" role="status" aria-live="polite">
      <span className="turn-pulse" aria-hidden="true" />
      <span>{label}</span>
      <time>{formatElapsed(seconds)}</time>
    </div>
  );
}

type ToolCategory = 'computer' | 'terminal' | 'files' | 'tool';
type MessageBlock =
  | { type: 'text'; key: string; text: string }
  | { type: 'tools'; key: string; category: ToolCategory; parts: DynamicToolUIPart[] };

function groupMessageParts(parts: UIMessage['parts']): MessageBlock[] {
  const blocks: MessageBlock[] = [];
  for (let index = 0; index < parts.length; index += 1) {
    const part = parts[index]!;
    if (isTextUIPart(part)) {
      blocks.push({ type: 'text', key: `text-${index}`, text: part.text });
      continue;
    }
    if (isDynamicToolUIPart(part)) {
      const category = toolCategory(part.toolName);
      const previous = blocks.at(-1);
      if (previous?.type === 'tools' && previous.category === category) previous.parts.push(part);
      else blocks.push({ type: 'tools', key: `tools-${part.toolCallId}`, category, parts: [part] });
      continue;
    }
  }
  return blocks;
}

export function ToolGroup({ category, parts }: { category: ToolCategory; parts: DynamicToolUIPart[] }) {
  const statuses = parts.map(toolStatus);
  const status = statuses.includes('error')
    ? 'error'
    : statuses.includes('interrupted')
      ? 'interrupted'
      : statuses.includes('running')
        ? 'running'
        : statuses.includes('queued')
          ? 'queued'
          : 'success';
  const label = ({ computer: 'Computer', terminal: 'Terminal', files: 'Files', tool: 'Tools' } as const)[category];
  const noun = category === 'computer' ? 'action' : category === 'terminal' ? 'command' : category === 'files' ? 'change' : 'call';
  return (
    <details className={`tool-group ${status}`} open={status !== 'success'} data-tool-group={category}>
      <summary>
        <span className="activity-mark" aria-hidden="true" />
        <strong>{label}</strong>
        <span>{parts.length} {noun}{parts.length === 1 ? '' : 's'}</span>
        <code>{status}</code>
      </summary>
      <div className="tool-group-body">
        {parts.map((part) => <ToolCard key={part.toolCallId} part={part} />)}
      </div>
    </details>
  );
}

export function ToolCard({ part }: { part: DynamicToolUIPart }) {
  const status = toolStatus(part);
  const input = 'input' in part ? part.input : undefined;
  const output = 'output' in part ? part.output : undefined;
  const error = 'errorText' in part ? part.errorText : undefined;
  return (
    <div className={`tool-card ${status}`} data-tool-status={status}>
      <div className="tool-summary">
        <span className="tool-icon" aria-hidden="true" />
        <strong>{friendlyTool(part.toolName)}</strong>
        <span className="tool-status">{status}</span>
      </div>
      {(input !== undefined || output !== undefined || error) && (
        <details>
          <summary>Details</summary>
          {input !== undefined && <pre><b>Arguments</b>{'\n'}{formatValue(input)}</pre>}
          {output !== undefined && <pre><b>Result</b>{'\n'}{formatValue(output)}</pre>}
          {error && <pre><b>Result</b>{'\n'}{error}</pre>}
        </details>
      )}
    </div>
  );
}

function toolStatus(part: DynamicToolUIPart): 'queued' | 'running' | 'success' | 'error' | 'interrupted' {
  if (part.state === 'input-streaming') return 'queued';
  if (part.state === 'input-available' || part.state === 'approval-requested') return 'running';
  if (part.state === 'output-available') return 'success';
  if (part.state === 'output-error' && /interrupt/iu.test(part.errorText)) return 'interrupted';
  if (part.state === 'output-error' || part.state === 'output-denied') return 'error';
  return 'running';
}

function toolCategory(toolName: string): ToolCategory {
  const normalized = toolName.toLowerCase();
  if (/computer|desktop_/u.test(normalized)) return 'computer';
  if (/exec_command|write_stdin|workspace_shell|terminal|shell/u.test(normalized)) return 'terminal';
  if (/apply_patch|view_image|workspace_(?:read|write|list)|filesystem|file_/u.test(normalized)) return 'files';
  return 'tool';
}

function CacheProofPanel({ proof }: { proof: PromptCacheProof }) {
  return (
    <section className="cache-proof-panel" aria-label="Prompt-cache proof results">
      <div className="cache-proof-heading">
        <div><span className="eyebrow">Live provider usage</span><strong>Prompt-cache A–F</strong></div>
        <time>{new Date(proof.generatedAt).toLocaleTimeString()}</time>
      </div>
      {proof.providers.map((provider) => (
        <article key={provider.provider} className={`cache-provider ${provider.status}`}>
          <header><strong>{provider.provider}</strong><span>{provider.model}</span><b>{provider.status}</b></header>
          <div className="cache-case-grid">
            {provider.cases.map((item) => (
              <div key={item.case} data-cache-case={item.case}>
                <strong>{item.case.replace('_', ' ')}</strong>
                <span>write {Math.max(item.usage.cacheCreationTokens, item.usage.cacheWriteTokens).toLocaleString()}</span>
                <span>read {item.usage.cachedTokens.toLocaleString()}</span>
                <small>{item.cacheSettingObservedOnWire ? 'wire setting observed' : 'wire setting absent'}</small>
              </div>
            ))}
          </div>
          {provider.blocker && <p>{provider.blocker}</p>}
        </article>
      ))}
    </section>
  );
}

function RecentWork({ sessions }: { sessions: PublicSession[] }) {
  return (
    <section className="recent-work">
      <h2>Recent work</h2>
      {sessions.slice(0, 5).map((session) => (
        <div key={session.id}>
          <span className={`state-dot ${session.workspaceLifecycle}`} />
          <strong>{session.title}</strong>
          <small>{session.turnState === 'running' ? 'Working' : workspaceStatus(session.workspaceLifecycle)} · {relativeTime(session.lastActiveAt)}</small>
        </div>
      ))}
      {sessions.length === 0 && <p>No sessions yet. Your first message starts one.</p>}
    </section>
  );
}

function SignInGate(props: {
  session: Extract<AuthSession, { authenticated: false }>;
  error: string | null;
  onAuthenticated: (session: Extract<AuthSession, { authenticated: true }>) => void;
  onError: (message: string | null) => void;
}) {
  const searchParams = new URLSearchParams(window.location.search);
  const firstAdminFlow = isFirstAdminAuthFlow(searchParams);
  const [authView, setAuthView] = useState<'options' | 'sso'>(() =>
    searchParams.get('auth') === 'sso' || searchParams.has('sso_error') ? 'sso' : 'options');
  const [email, setEmail] = useState('');
  const [ssoEmail, setSsoEmail] = useState(() =>
    searchParams.get('sso_email') ?? (searchParams.has('sso_error') ? 'a@a.com' : ''));
  const [ssoError, setSsoError] = useState<string | null>(() =>
    searchParams.has('sso_error') ? ssoConfigurationError : null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const googleFailed = searchParams.has('auth_error');

  return (
    <div className="auth-backdrop">
      <AuthLayout
        className="cyrus-auth-layout"
        brand={<CyrusBrand logoHeight={26} />}
        aside={<SignInMarketingAside />}
        footer={<AuthLegalFooter />}
      >
        <div className="cyrus-auth-card" aria-labelledby="cyrus-sign-in-title">
          {authView === 'sso' ? (
            <SsoSignInForm
              email={ssoEmail}
              error={ssoError}
              onBack={() => {
                setSsoError(null);
                setAuthView('options');
              }}
              onEmailChange={(value) => {
                setSsoEmail(value);
                if (ssoError) setSsoError(null);
              }}
              onSubmit={() => setSsoError(ssoConfigurationError)}
            />
          ) : (
            <>
              <div className="cyrus-auth-title">
                <h1 id="cyrus-sign-in-title">Welcome to Cyrus</h1>
                <p>Sign in to your account to get started</p>
              </div>
              {(props.error || googleFailed) && (
                <div className="auth-error" role="alert">
                  {props.error ?? 'Google sign-in could not be completed. Please try again.'}
                </div>
              )}
              <div className="cyrus-auth-options">
                <button
                  type="button"
                  className="cyrus-auth-choice cyrus-auth-choice-primary"
                  onClick={() => props.onError('GitHub sign-in is not configured for this environment yet.')}
                >
                  <AuthProviderIcon provider="github" surface="dark" />
                  <span>Continue with GitHub</span>
                  {!firstAdminFlow ? <span className="cyrus-auth-badge">Recommended</span> : null}
                </button>
                {props.session.googleEnabled && (
                  <>
                    <AuthDivider />
                    <a className="cyrus-auth-choice" href="/auth/google">
                      <AuthProviderIcon provider="google" />
                      <span>Continue with Google</span>
                    </a>
                  </>
                )}
                {!firstAdminFlow && (
                  <button
                    type="button"
                    className="cyrus-auth-choice"
                    onClick={() => {
                      props.onError(null);
                      setAuthView('sso');
                    }}
                  >
                    <AuthProviderIcon provider="sso" />
                    <span>Continue with SSO</span>
                  </button>
                )}
              </div>
            </>
          )}
          {authView === 'options' && (props.session.passwordEnabled || props.session.developmentEmailLogin) && (
            <div className="cyrus-auth-alternate">
              <AuthDivider label={props.session.googleEnabled || !firstAdminFlow ? 'or continue another way' : 'or'} />
              {props.session.passwordEnabled && (
                <form onSubmit={(event) => {
                  event.preventDefault();
                  if (busy) return;
                  setBusy(true);
                  props.onError(null);
                  void passwordSignIn(username, password)
                    .then((session) => {
                      if (!session.authenticated) throw new Error('The sign-in session was not established.');
                      setUsername('');
                      setPassword('');
                      props.onAuthenticated(session);
                    })
                    .catch((error: Error) => props.onError(error.message))
                    .finally(() => setBusy(false));
                }}>
                  <label>
                    <span>Username</span>
                    <Input
                      type="text"
                      autoComplete="username"
                      required
                      maxLength={128}
                      value={username}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => setUsername(event.target.value)}
                      autoFocus={!props.session.googleEnabled}
                    />
                  </label>
                  <label>
                    <span>Password</span>
                    <Input
                      type="password"
                      autoComplete="current-password"
                      required
                      maxLength={1024}
                      value={password}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => setPassword(event.target.value)}
                    />
                  </label>
                  <Button submit expanding pending={busy} disabled={busy || !username.trim() || !password}>
                    Sign in
                  </Button>
                </form>
              )}
              {props.session.passwordEnabled && props.session.developmentEmailLogin && (
                <AuthDivider label="or use local development access" />
              )}
              {props.session.developmentEmailLogin && (
                <form onSubmit={(event) => {
                  event.preventDefault();
                  if (busy) return;
                  setBusy(true);
                  props.onError(null);
                  void developmentSignIn(email)
                    .then((session) => {
                      if (!session.authenticated) throw new Error('The local sign-in session was not established.');
                      setEmail('');
                      props.onAuthenticated(session);
                    })
                    .catch((error: Error) => props.onError(error.message))
                    .finally(() => setBusy(false));
                }}>
                  <label>
                    <span>Email address</span>
                    <Input
                      type="email"
                      autoComplete="email"
                      required
                      maxLength={320}
                      placeholder="you@example.com"
                      value={email}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
                      autoFocus={!props.session.googleEnabled && !props.session.passwordEnabled}
                    />
                  </label>
                  <Button submit expanding pending={busy} disabled={busy || !email.trim()}>
                    Continue with email
                  </Button>
                  <small>Development only. Production builds disable this route.</small>
                </form>
              )}
            </div>
          )}
          {!props.session.googleEnabled && !props.session.passwordEnabled && !props.session.developmentEmailLogin && (
            <div className="auth-error" role="status">
              Sign-in has not been configured for this environment.
            </div>
          )}
        </div>
      </AuthLayout>
    </div>
  );
}

function SsoSignInForm(props: {
  email: string;
  error: string | null;
  onBack: () => void;
  onEmailChange: (value: string) => void;
  onSubmit: () => void;
}) {
  return (
    <form
      className="cyrus-auth-sso-form"
      onSubmit={(event) => {
        event.preventDefault();
        props.onSubmit();
      }}
    >
      <div className="cyrus-auth-title">
        <h1 id="cyrus-sign-in-title">Sign in with SSO</h1>
        <p>Enter your work email to continue</p>
      </div>
      <label className="cyrus-auth-sso-label">
        <span>Email address</span>
        <Input
          type="email"
          autoComplete="email"
          required
          maxLength={320}
          value={props.email}
          invalid={Boolean(props.error)}
          onChange={(event: ChangeEvent<HTMLInputElement>) => props.onEmailChange(event.target.value)}
        />
      </label>
      {props.error && (
        <div className="cyrus-auth-sso-error" role="alert">
          {props.error}
        </div>
      )}
      <button
        type="submit"
        className="cyrus-auth-choice cyrus-auth-sso-submit"
        disabled={Boolean(props.error)}
      >
        <AuthProviderIcon provider="sso" />
        Continue with SSO
      </button>
      <button type="button" className="cyrus-auth-back" onClick={props.onBack}>
        Back
      </button>
    </form>
  );
}

function AuthDivider({ label = 'Or' }: { label?: string }) {
  return <div className="auth-divider"><span>{label}</span></div>;
}

function AuthProviderIcon({
  provider,
  surface = 'light',
}: {
  provider: AppIconName | 'sso';
  surface?: AppIconSurface;
}) {
  if (provider === 'sso') {
    return (
      <span className="cyrus-auth-icon sso" aria-hidden="true">
        <Key width={18} height={18} strokeWidth={2} />
      </span>
    );
  }

  return (
    <img
      className={`cyrus-auth-app-icon ${provider}`}
      src={getAppIconUrl(provider, surface)}
      alt=""
      aria-hidden="true"
      draggable={false}
    />
  );
}

function SignInMarketingAside() {
  return (
    <div className="cyrus-auth-aside-content">
      <div className="cyrus-auth-marketing">
        <h2>Your org-level harness</h2>
        <p>Set up access bundles, connect your tools, and let Cyrus ship code across your repositories automatically.</p>
      </div>
      <div className="cyrus-auth-trust">
        <span>Trusted by teams worldwide</span>
        <div className="cyrus-auth-logo-grid" aria-label="Trusted customer logos">
          {['Vanta', 'Cyrus partner', 'BNY', 'GSR', 'Pearson', 'EquipmentShare'].map((name) => (
            <div key={name} aria-label={name}>
              <img src={getAppIconUrl('github', 'light')} alt="" aria-hidden="true" draggable={false} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AuthLegalFooter() {
  return (
    <>
      By signing in, you agree to our <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a>
    </>
  );
}

function isFirstAdminAuthFlow(params: URLSearchParams): boolean {
  return params.has('invite')
    || params.has('invitation')
    || params.get('flow') === 'first-admin'
    || params.get('auth_flow') === 'first-admin';
}

const ssoConfigurationError = 'We did not find an organization with a valid configuration for your email address. To configure SSO sign-in, you first need to sign-in using one of the public providers and then configure SSO.';

function Spinner() {
  return <span className="spinner" aria-hidden="true" />;
}

function workspaceStatus(status: PublicSession['workspaceLifecycle']): string {
  return ({ starting: 'Starting workspace…', ready: 'Ready', suspended: 'Suspended', resuming: 'Restoring your workspace…', replacing: 'Restoring your workspace…', failed: 'Workspace needs attention', terminated: 'Ended' } satisfies Record<PublicSession['workspaceLifecycle'], string>)[status];
}

function deploymentRailStatus(deployment: CustomerDeploymentStatus | null): string {
  if (!deployment) return 'Create secure infrastructure';
  if (deployment.status === 'connected') return `Connected · ${deployment.region}`;
  if (deployment.status === 'disconnected') return 'Connection interrupted';
  if (deployment.status === 'pending') return 'Deployment in progress';
  return 'Setup required';
}

function githubRailStatus(
  installations: GithubAppStatus['installations'],
  deployment: CustomerDeploymentStatus | null,
): string {
  const account = installations.length === 1
    ? installations[0]?.accountLogin ?? '1 account'
    : `${installations.length} accounts`;
  if (deployment?.status !== 'connected') return `${account} · Connect AWS next`;
  if (!deployment.policyRevision) return `${account} · Choose repositories`;
  if (deployment.policyStatus === 'active') {
    return `${account} · Policy r${deployment.policyRevision} active`;
  }
  if (deployment.policyStatus === 'failed') {
    return `${account} · Policy needs attention`;
  }
  return `${account} · Policy r${deployment.policyRevision} applying`;
}

function turnStatus(status: PublicSession['turnState']): string {
  return ({ idle: 'Ready', running: 'Working', interrupting: 'Stopping…' } satisfies Record<PublicSession['turnState'], string>)[status];
}

function friendlyTool(tool: string): string {
  return tool.replaceAll('_', ' ').replace(/\b\w/gu, (letter) => letter.toUpperCase());
}

function relativeTime(timestamp: string): string {
  const seconds = Math.max(1, Math.round((Date.now() - new Date(timestamp).getTime()) / 1000));
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86_400)}d ago`;
}

function formatElapsed(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

function formatValue(value: unknown): string {
  if (typeof value === 'string') return value;
  try { return JSON.stringify(value, null, 2); }
  catch { return '[unavailable]'; }
}

function runTransition(update: () => void): void {
  const documentWithTransition = document as Document & {
    startViewTransition?: (callback: () => void) => void;
  };
  if (documentWithTransition.startViewTransition) documentWithTransition.startViewTransition(update);
  else update();
}
